// Monitoramento de erros no front. [[project-error-monitoring]]
//   - reportClientError(): envia um erro pro /api/client-errors (dedupe + cap).
//   - installErrorReporter(): captura window.onerror + unhandledrejection.
//   - <ErrorBoundary>: isola um subtree — se quebrar no render, loga e mostra
//     um fallback em vez de derrubar a tela inteira.

import { Component, type ComponentChildren } from "preact";

const sentKeys = new Set<string>();

export function reportClientError(
  message: string,
  stack?: string | null,
  meta?: Record<string, unknown>
): void {
  try {
    const key = (message || "") + "|" + (stack || "").slice(0, 120);
    if (sentKeys.has(key)) return; // dedupe por sessão
    sentKeys.add(key);
    if (sentKeys.size > 50) sentKeys.clear();
    void fetch("/api/client-errors", {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        message: String(message).slice(0, 2000),
        stack: stack ? String(stack).slice(0, 8000) : undefined,
        path:
          typeof location !== "undefined"
            ? location.pathname + location.search
            : undefined,
        meta
      })
    }).catch(() => {
      /* logging nunca quebra o app */
    });
  } catch {
    /* ignore */
  }
}

let installed = false;
export function installErrorReporter(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener("error", (e) => {
    const ev = e as ErrorEvent;
    reportClientError(ev.message || "window.onerror", ev.error?.stack, {
      type: "error",
      filename: ev.filename,
      line: ev.lineno
    });
  });
  window.addEventListener("unhandledrejection", (e) => {
    const r = (e as PromiseRejectionEvent).reason;
    reportClientError(
      (r && (r.message as string)) || String(r) || "unhandledrejection",
      r && r.stack,
      { type: "unhandledrejection" }
    );
  });
}

interface Props {
  children: ComponentChildren;
  fallback?: ComponentChildren;
  label?: string;
}
interface State {
  hasError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    const err = error as { message?: string; stack?: string };
    reportClientError(err?.message || "render error", err?.stack, {
      type: "boundary",
      label: this.props.label
    });
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }
    return this.props.children;
  }
}
